<template>
	<button class="link-button"
			:class="classes"
			@click="handleClick"
			:is="node"
			:type="buttonType"
			@mouseup="onMouseup"
	>
		<link-loading type="beta" class="link-button-icon link-button-icon-left" v-if="lv_loading"/>
		<link-icon :icon="icon" v-if="!!icon && iconPosition === 'left'" :loading="iconLoading" class="link-button-icon link-button-icon-left"/>
		<slot><span class="link-button-label">{{label}}</span></slot>
		<link-icon :icon="icon" v-if="!!icon && iconPosition === 'right'" :loading="iconLoading" class="link-button-icon link-button-icon-right"/>
	</button>
</template>

<script>
	import {EditMixin} from '../mixins';

	export default {
		name: 'link-button',
		mixins: [EditMixin],
		props: {
			type: {type: String, default: 'fill',},                                                             //盒子类型：fill,line,none,dashed
			color: {type: String, default: 'primary',},                                                         //盒子颜色：primary,success,warn,error,info
			size: {type: String, default: 'default',},                                                          //盒子大小：large,default,small
			shape: {type: String, default: 'none',},                                                            //盒子形状：none,fillet,round
			dashed: {type: Boolean,},                                                                           //边线是否为虚线
			label: {},                                                                                          //按钮文本
			icon: {type: String,},                                                                              //按钮图标
			iconLoading: {type: Boolean,},                                                                      //按钮显示loading图标
			iconPosition: {type: String, default: 'left',},                                                     //按钮位置
			iconOnly: {type: Boolean,},                                                                         //只有图标
			long: {type: Boolean},                                                                              //长按钮
			shadeOnClick: {type: Boolean, default: false},                                                      //点击时抖动
			clickEffect: {type: Boolean, default: true},                                                        //点击涟漪效果
			noBorder: {type: Boolean, default: false},                                                          //无边框
			noPadding: {type: Boolean, default: false},                                                         //无内边距
			node: {type: String, default: 'button'},                                                            //显示的html节点
			preventFastClickDuration: {type: Number, default: 1000},                                             //是否防止快速多次点击
			availableTimes: {type: Number,},                                                                    //最多可点击次数
			loading: {type: Boolean},                                                                           //按钮是否处于加载中状态
			synchronize: {type: Boolean, default: true},                                                        //是否启用异步控制功能
			buttonType: {type: String, default: 'button'},                                                      //按钮类型，button，submit等
			active: {type: Boolean},                                                                            //按钮是否处于激活状态
			clickOutline: {type: Boolean, default: null},														//点击的时候是否要有outline样式
		},
		computed: {
			classes() {
				return [
					`link-button-${!!this.dashed ? 'line' : this.type}`,
					`link-button-color-${this.color}`,
					`link-button-size-${!!this.groupSize ? this.groupSize : this.size}`,
					`link-button-shape-${!!this.iconOnly ? 'round ' : this.shape}`,
					`link-button-outline-${this.outlineFlag}`,
					{
						'link-button-dashed': !!this.dashed,
						'link-button-active': !!this.active,
						'link-button-long': !!this.long,
						'link-button-disabled': !!this.lv_disabled,
						'link-button-shade-on-click': !!this.shadeOnClick,
						'link-button-no-border': !!this.noBorder,
						'link-button-no-padding': !!this.noPadding,
						[`link-button-icon-only-${this.size}`]: !!this.iconOnly,
					}
				];
			},
			groupSize() {
				return (this.$parent.$options.name === 'link-button-group') ? this.$parent.size : null;
			},
		},
		data() {

			const onClick = async (e) => {
				if (!!this.lv_readonly || this.lv_disabled || this.lv_loading) return
				if (!this.$listeners.click) return
				await this.$lv.nextTick()
				await this.$lv.$utils.delay(0)
				try {
					if (this.synchronize) {
						this.lv_loading = true
						await this.$listeners.click(e)
					} else {
						this.$emit('click', e)
					}
				} catch (e) {
					console.error(e)
				} finally {
					this.lv_loading = false
					this.clickTimes++
					if (this.clickTimes === this.availableTimes) this.lv_disabled = true
				}
			}
			const handleClick = this.preventFastClickDuration == null ? onClick : this.$lv.$utils.throttle(onClick, this.preventFastClickDuration)
			return {
				timer: null,
				clickTimes: 0,
				lv_loading: this.loading,
				outlineFlag: 'hide',
				handleClick,
			}
		},
		methods: {
			onMouseup() {
				if (!!this.lv_readonly || this.lv_disabled || this.lv_loading) return

				if (this.clickOutline !== false && this.type !== 'none') {
					this.outlineFlag = 'start'
					if (!!this.outlineTimer) clearTimeout(this.outlineTimer)
					this.outlineTimer = setTimeout(() => {
						this.outlineFlag = 'stop'
						this.outlineTimer = setTimeout(() => {
							this.outlineFlag = 'hide'
						}, 200)
					}, 200)
				}
			},
		},
	};
</script>

<style lang="scss">
	@include themeify {
		.link-button {
			border: none;
			background: none;
			padding: 0 8px;
			margin: 0;
			cursor: pointer;
			box-sizing: border-box;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			vertical-align: bottom;
			overflow: hidden;
			transition: all 0.15s linear;
			min-width: 72px;

			&.link-button-outline-hide {
				outline: rgba($color-primary, 0) solid 0;
			}

			&.link-button-outline-start {
				outline: rgba($color-primary, 0.45) solid 3px;
			}

			&.link-button-outline-stop {
				outline: rgba($color-primary, 0) solid 6px;
			}

			.link-button-icon {
				&.link-button-icon-left {
					margin-right: 6px;
				}

				&.link-button-icon-right {
					margin-left: 6px;
				}
			}

			/*size*/
			@include pl-sizes('') {
				&.link-button-size-#{$key} {
					height: $value - 2;
					font-size: ceil($value/2.5);
				}
				&.link-button-icon-only-#{$key} {
					width: $value;
					min-width: unset;
					padding: 0;

					.link-icon {
						margin: 0 !important;
					}
				}
			}
			/*shape*/
			@include pl-shapes(link-button) {
				border-radius: $value;
			}
			/*fill type*/
			&.link-button-fill {
				@include pl-colors(link-button) {
					background-color: $value;
					border: solid $border-size $value;
					color: white;
					&.link-button-active {
						background-color: $pl-color-deep;
					}
				}
			}

			/*line type*/
			&.link-button-line {
				@include pl-colors(link-button) {
					border: solid $border-size $value;
					background: transparent;
					color: $value;
				}

				&.link-button-dashed {
					border-style: dashed;
				}
			}

			/*none type*/
			&.link-button-none {
				@include pl-colors(link-button) {
					border: solid $border-size transparent;
					background: transparent;
					color: $color-normal-input;
				}

				.link-button-icon {
					margin: 0;
					position: relative;
					top: 1px;
				}
			}

			&.link-button-shade-on-click {
				/*tap style*/
				&:active {
					transform: scale(0.9);
				}
			}

			&.link-button-long {
				width: 100%;
			}

			&.link-button-no-border {
				border: none !important;
			}

			&.link-button-no-padding {
				padding-left: 0 !important;
				padding-right: 0 !important;
			}

			&.link-button-disabled {
				background-color: $color-normal-disabled !important;
				color: $color-normal-disabled-text !important;;
				border-color: $color-info !important;
				cursor: not-allowed;
			}

			&.link-button-readonly {
				color: $color-normal-disabled-text !important;
				cursor: not-allowed;
			}
		}

		.link-button-group {
			display: inline-flex;
			box-sizing: border-box;

			&.link-button-group-vertical {
				flex-direction: column;
				/*shape*/
				.link-button {
					border-radius: 0;

					&:not(:first-child) {
						border-top-color: transparent;

						&.link-button-fill {
							@include pl-colors(link-button) {
								border-top-color: mix($value, white, 10%);
							}
						}
					}
				}

				@include pl-shapes(link-button-group) {
					.link-button:first-child {
						border-top-left-radius: $value;
						border-top-right-radius: $value;
					}
					.link-button:last-child {
						border-bottom-left-radius: $value;
						border-bottom-right-radius: $value;
					}
				}
			}

			&.link-button-group-horizontal {
				flex-direction: row;

				.link-button {
					border-radius: 0;

					&:not(:first-child) {

						border-left-color: transparent;

						&.link-button-fill {
							@include pl-colors(link-button) {
								border-left-color: mix($value, white, 10%);
							}
						}
					}
				}

				/*shape*/
				@include pl-shapes(link-button-group) {
					.link-button:first-child {
						border-top-left-radius: $value;
						border-bottom-left-radius: $value;
					}
					.link-button:last-child {
						border-top-right-radius: $value;
						border-bottom-right-radius: $value;
					}
				}
			}
		}

		button.link-button {
			/*fill type*/
			&.link-button-fill {
				@include pl-colors(link-button) {
					&:active {
						opacity: 0.5;
					}
				}
			}

			/*line type*/
			&.link-button-line {
				@include pl-colors(link-button) {
					&:active {
						opacity: 0.5;
					}
				}
			}

			/*none type*/
			&.link-button-none {
				@include pl-colors(link-button) {
					&:active {
						opacity: 0.5;
					}
				}
			}
		}

		.table-button {
			.link-button {
				padding: 0 !important;
				min-width: initial !important;
				height: initial;
				background-color: transparent !important;
				border: none !important;

				&:not(.link-button-disabled) {
					color: pl-var(color-primary) !important;
				}

				& + .link-button {
					margin-left: 9px;
					position: relative;
					overflow: visible;

					&:after {
						position: absolute;
						left: -6px;
						top: 3px;
						bottom: 1px;
						width: 1px;
						background-color: #e9e9e9;
						content: '';
					}
				}
			}
		}
	}
</style>
